/** Angle-change review (spec D12, slice 3): one dialog for retire / restore / big edit / add.
 * Says what the action does, offers the project question as a choice (keep / redraft /
 * write your own), and has one primary button named for the action. With `redraft={false}`
 * (retire down to one angle) the project question is shown unchanged and nothing is drafted.
 * Stays mounted until the caller's save succeeds (failed saves keep the text). */
import { useEffect, useRef, useState, type ReactNode, type Ref } from "react";
import { Check, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { useDraftProjectQuestion, type AngleText } from "@/hooks/useFacets";
import { plainText } from "@/lib/conceptGuidance";
import { sameWords, wordDiff } from "@/lib/wordDiff";
import type { ProjectQuestion } from "@/types/concept";

/** Backend ProjectQuestion limits (schemas/facet.py). */
const MAX_ABSTRACT = 5000;
const MAX_KEYWORDS = 1000;

type Draft = { abstract: string; keywords: string };
type DraftState = { status: "pending" | "done" | "error"; data?: Draft };
type Choice = "keep" | "use" | "edit";

interface Props {
  open: boolean;
  conceptId: string;
  /** The angle set AFTER the change. */
  angles: AngleText[];
  currentAbstract: string;
  currentKeywords: string;
  title: string;
  /** Plain-words list of what the action does. */
  effects?: ReactNode[];
  /** Shown beside the footer buttons (e.g. where restore lives). */
  footnote?: ReactNode;
  /** false: no redraft; the project question stays as it is. */
  redraft?: boolean;
  /** Action verb on the primary button: "Retire" / "Restore" / "Save" / "Add". */
  confirmLabel: string;
  submitting: boolean;
  /** undefined = no project_question (keep, with an empty current text, or redraft={false}). */
  onSubmit: (pq: ProjectQuestion | undefined) => void;
  onCancel: () => void;
}

export function ProjectQuestionReview({
  open, conceptId, angles, currentAbstract, currentKeywords, title, effects, footnote, redraft = true,
  confirmLabel, submitting, onSubmit, onCancel,
}: Props) {
  const draft = useDraftProjectQuestion(conceptId);
  const [choice, setChoice] = useState<Choice>("keep");
  const [text, setText] = useState<string | null>(null);
  const [result, setResult] = useState<DraftState>({ status: "pending" });
  const keepRef = useRef<HTMLInputElement>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);
  const anglesKey = JSON.stringify(angles);
  // Angle set frozen at open: later prop changes (optimistic cache writes, polls)
  // must not re-draft or drop the user's edit — least of all mid-submit.
  const frozenKey = useRef<string | null>(null);
  // The one draft request for this open. Rendering from local state fed by
  // this promise (not draft.data/isPending) survives StrictMode's
  // mount→cleanup→mount: useMutation's observer resubscribes and drops the
  // in-flight result, and the frozen key forbids re-firing the LLM call.
  const inflight = useRef<{ key: string; promise: Promise<Draft> } | null>(null);

  useEffect(() => {
    if (!open) {
      frozenKey.current = null;
      inflight.current = null;
      setResult({ status: "pending" });
      return;
    }
    if (frozenKey.current === null) {
      frozenKey.current = anglesKey;
      setChoice("keep");
      setText(null);
      setResult({ status: "pending" });
    }
    if (!redraft) return;
    const key = frozenKey.current;
    if (inflight.current?.key !== key) inflight.current = { key, promise: draft.mutateAsync(angles) };
    let live = true;
    inflight.current.promise.then(
      (data) => { if (live) setResult({ status: "done", data }); },
      () => { if (live) setResult({ status: "error" }); },
    );
    return () => { live = false; };
    // Draft once per open; angles are frozen for the life of the open dialog.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, anglesKey, redraft]);

  const current = plainText(currentAbstract);
  const proposal = result.data;
  const unchanged = !!proposal && sameWords(proposal.abstract, current);
  const usable = !!proposal && !unchanged && proposal.abstract.length <= MAX_ABSTRACT;
  const keywords = (proposal?.keywords ?? currentKeywords).slice(0, MAX_KEYWORDS);
  const editText = text ?? (usable ? proposal.abstract : current).slice(0, MAX_ABSTRACT);

  const pick = (c: Choice) => {
    // Writing your own starts from the redraft (or yours), captured once.
    if (c === "edit" && text === null) setText(editText);
    setChoice(c);
  };
  // A redraft that later turns out unusable can't stay selected.
  const effective: Choice = choice === "use" && !usable ? "keep" : choice;
  const canSubmit = !submitting && (effective !== "edit" || editText.trim().length > 0);

  const submit = () => {
    if (!canSubmit) return;
    if (!redraft) return onSubmit(undefined);
    if (effective === "use") return onSubmit({ abstract: proposal!.abstract, keywords, choice: "use" });
    if (effective === "edit") return onSubmit({ abstract: editText.trim(), keywords, choice: "edit" });
    onSubmit(current ? { abstract: currentAbstract, keywords: currentKeywords, choice: "keep" } : undefined);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !submitting && onCancel()}>
      <DialogContent
        className="sm:max-w-3xl"
        onOpenAutoFocus={(e) => {
          // Focus the selected choice (or the action), never Cancel.
          e.preventDefault();
          (keepRef.current ?? confirmRef.current)?.focus();
        }}
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription className={effects?.length ? "sr-only" : undefined}>
            Every paper's overall score is graded against the project question.
          </DialogDescription>
        </DialogHeader>

        {!!effects?.length && (
          <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
            {effects.map((e, i) => <li key={i}>{e}</li>)}
          </ul>
        )}

        <fieldset className="min-w-0 space-y-2" disabled={submitting}>
          <legend className="mb-2 text-xs font-medium text-muted-foreground">
            Project question {redraft ? "· every paper's overall score is graded against it" : "· unchanged"}
          </legend>
          {!redraft ? (
            <p className="whitespace-pre-wrap rounded-md border p-3 text-sm">{current || "—"}</p>
          ) : (
            <>
              <Option name="pq" value="keep" checked={effective === "keep"} onPick={pick} inputRef={keepRef}
                title="Keep my question">
                {current || "—"}
              </Option>

              {result.status === "pending" && (
                <Option name="pq" value="use" checked={false} onPick={pick} disabled title="Use the redraft">
                  <span className="inline-flex items-center gap-1.5"><Loader2 className="size-3.5 animate-spin" />Drafting…</span>
                </Option>
              )}
              {result.status === "error" && (
                <p className="px-1 text-sm text-muted-foreground">Couldn't draft a new version. Keep yours or write your own.</p>
              )}
              {unchanged && (
                <p className="flex items-center gap-1.5 px-1 text-sm text-[#a6e3a1]">
                  <Check className="size-3.5" />No change needed: the redraft matches your question.
                </p>
              )}
              {proposal && !unchanged && (
                <Option name="pq" value="use" checked={effective === "use"} onPick={pick} disabled={!usable}
                  title="Use the redraft">
                  <Diff before={current} after={proposal.abstract} />
                  {!usable && (
                    <span className="mt-1 block text-xs">This draft is too long to use as-is. Write your own to edit it down.</span>
                  )}
                </Option>
              )}

              <Option name="pq" value="edit" checked={effective === "edit"} onPick={pick} title="Write my own">
                {effective !== "edit" && (usable ? "Starts from the redraft." : "Starts from your question.")}
              </Option>
              {effective === "edit" && (
                <Textarea aria-label="Project question" rows={6} maxLength={MAX_ABSTRACT} value={editText}
                  onChange={(e) => setText(e.target.value)} />
              )}
            </>
          )}
        </fieldset>

        <DialogFooter className="items-center gap-2">
          {footnote && <p className="mr-auto text-xs text-muted-foreground">{footnote}</p>}
          <Button variant="ghost" onClick={onCancel} disabled={submitting}>Cancel</Button>
          <Button ref={confirmRef} disabled={!canSubmit} onClick={submit}>
            {submitting && <Loader2 className="size-3.5 animate-spin" />}{confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Option({ name, value, checked, onPick, disabled, title, children, inputRef }: {
  name: string;
  value: Choice;
  checked: boolean;
  onPick: (c: Choice) => void;
  disabled?: boolean;
  title: string;
  children?: ReactNode;
  inputRef?: Ref<HTMLInputElement>;
}) {
  return (
    <label
      className={`flex gap-2.5 rounded-md border p-3 text-sm ${checked ? "border-primary bg-primary/5" : ""} ${disabled ? "opacity-60" : "cursor-pointer"}`}
    >
      <input
        ref={inputRef} type="radio" name={name} value={value} checked={checked} disabled={disabled}
        onChange={() => onPick(value)} className="mt-1 size-3.5 shrink-0 self-start accent-[var(--color-primary)]"
      />
      <span className="min-w-0">
        <span className="block font-medium">{title}</span>
        {children && <span className="block whitespace-pre-wrap text-muted-foreground">{children}</span>}
      </span>
    </label>
  );
}

function Diff({ before, after }: { before: string; after: string }) {
  return (
    <span>
      {wordDiff(before, after).map((p, i) =>
        p.kind === "same" ? <span key={i}>{p.text}</span>
          : p.kind === "del" ? <del key={i} className="bg-destructive/10 text-destructive">{p.text}</del>
            : <ins key={i} className="bg-[#a6e3a1]/15 text-foreground no-underline">{p.text}</ins>,
      )}
    </span>
  );
}
